import { Check, Clear } from '@mui/icons-material';
import { Alert, Box, Button, DialogActions, DialogContent, Typography } from '@mui/material';
import { useRef, useState, type MouseEvent, type TouchEvent } from 'react';

import useTranslate from 'hooks/useTranslate';

interface SignatureModalProps {
    onClose: () => void;
    onSign: (signature: string) => void;
    isSubmitting?: boolean;
}

const SignatureBoard = ({ onClose, onSign, isSubmitting = false }: SignatureModalProps) => {
    const { translate } = useTranslate();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);

    const startDrawing = (event: MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(event.clientX - rect.left, event.clientY - rect.top);
        setIsDrawing(true);
    };

    const draw = (event: MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        ctx.lineTo(event.clientX - rect.left, event.clientY - rect.top);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
        setHasSignature(true);
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const handleTouchStart = (event: TouchEvent<HTMLCanvasElement>) => {
        event.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const touch = event.touches[0];
        ctx.beginPath();
        ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
        setIsDrawing(true);
    };

    const handleTouchMove = (event: TouchEvent<HTMLCanvasElement>) => {
        event.preventDefault();
        if (!isDrawing) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const touch = event.touches[0];
        ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
        setHasSignature(true);
    };

    const handleTouchEnd = () => {
        setIsDrawing(false);
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    };

    const applySignature = () => {
        const canvas = canvasRef.current;
        if (!canvas || !hasSignature) return;

        onSign(canvas.toDataURL('image/png'));
    };

    const handleClose = () => {
        if (!isSubmitting) {
            clearSignature();
            onClose();
        }
    };

    return (
        <Box>
            <DialogContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                    {translate('pleaseSignInTheBox')}
                </Alert>

                <Box
                    sx={{
                        border: 2,
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        backgroundColor: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {translate('signHere')}
                    </Typography>
                    <canvas
                        ref={canvasRef}
                        width={600}
                        height={300}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        style={{
                            border: '1px dashed #ccc',
                            cursor: 'default',
                            touchAction: 'none',
                            maxWidth: '100%',
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={clearSignature}
                    startIcon={<Clear />}
                    disabled={!hasSignature || isSubmitting}
                >
                    {translate('clearSignature')}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={handleClose} disabled={isSubmitting}>
                    {translate('cancel')}
                </Button>
                <Button
                    onClick={applySignature}
                    variant="contained"
                    startIcon={<Check />}
                    disabled={!hasSignature || isSubmitting}
                >
                    {isSubmitting ? translate('signing') : translate('applySignature')}
                </Button>
            </DialogActions>
        </Box>
    );
};

export default SignatureBoard;
